import {classes} from '@bitstillery/common/lib/utils'
import {Icon} from '@bitstillery/common/components/ui/icon/icon'
import {MithrilComponent} from '@bitstillery/mithril'
import {modelref_adapter, modelref_assign} from '@bitstillery/common/lib/store'
import m from '@bitstillery/mithril'

import type {FieldNumberAttrs} from '@bitstillery/common/types/field'
import {failure_message} from '@bitstillery/common/lib/validation'

export class FieldNumber extends MithrilComponent<FieldNumberAttrs> {
    view(vnode: m.Vnode<FieldNumberAttrs>) {
        const {model} = vnode.attrs
        if (!model) {
            return null
        }
        const {model_value, model_ref} = modelref_adapter(model)
        if (!model_ref) {
            return null
        }
        const {validation} = vnode.attrs

        if (vnode.attrs.validation && model_value) {
            vnode.attrs.validation.dirty = true
        }

        // Add human readable description to the validation object.
        if (validation && vnode.attrs.label) {
            validation.description = vnode.attrs.label
        }

        const invalid = validation ? validation._invalid : false
        const {disabled} = vnode.attrs

        return (
            <div
                className={classes('c-field-number', vnode.attrs.composed ? null : 'field', vnode.attrs.className, {
                    disabled: disabled,
                    invalid: validation && invalid && validation.dirty,
                    valid: validation && !invalid && validation.dirty,
                })}
            >
                {vnode.attrs.label && (
                    <label>
                        {vnode.attrs.label}
                        {vnode.attrs.icon && <Icon name={vnode.attrs.icon} />}
                        {validation && <span className='validation'>{validation.label}</span>}
                    </label>
                )}

                <div className='input-wrapper'>
                    <input
                        autocomplete={vnode.attrs.autocomplete}
                        autofocus={vnode.attrs.autofocus}
                        disabled={vnode.attrs.disabled}
                        onkeydown={(e: KeyboardEvent) => {
                            const {model_value: key_model_value} = modelref_adapter(vnode.attrs.model)
                            // Determine decimal places: use provided decimals or infer from step
                            let decimals = vnode.attrs.decimals
                            if (decimals === undefined && vnode.attrs.step) {
                                const step_str = String(vnode.attrs.step)
                                const decimal_part = step_str.split('.')[1]
                                decimals = decimal_part ? decimal_part.length : 0
                            }

                            // Round the current value to avoid floating-point accumulation errors
                            let rounded_value = Number(key_model_value)
                            if (decimals !== undefined && key_model_value != null) {
                                rounded_value = Number(Number(key_model_value).toFixed(decimals))
                            }

                            if (e.key === 'ArrowDown') {
                                e.preventDefault()
                                if (vnode.attrs.onarrowdown) {
                                    vnode.attrs.onarrowdown(e, rounded_value)
                                } else {
                                    const step_size = vnode.attrs.step || 1
                                    let new_value = rounded_value - step_size
                                    // Round the result
                                    if (decimals !== undefined) {
                                        new_value = Number(new_value.toFixed(decimals))
                                    }
                                    if (vnode.attrs.min === undefined) {
                                        modelref_assign(model_ref, new_value)
                                    } else {
                                        if (new_value >= vnode.attrs.min) {
                                            modelref_assign(model_ref, new_value)
                                        } else {
                                            modelref_assign(model_ref, vnode.attrs.min)
                                        }
                                    }
                                }
                            } else if (e.key === 'ArrowUp') {
                                e.preventDefault()
                                if (vnode.attrs.onarrowup) {
                                    vnode.attrs.onarrowup(e, rounded_value)
                                } else {
                                    const step_size = vnode.attrs.step || 1
                                    let new_value = rounded_value + step_size
                                    // Round the result
                                    if (decimals !== undefined) {
                                        new_value = Number(new_value.toFixed(decimals))
                                    }

                                    if (vnode.attrs.max === undefined) {
                                        modelref_assign(model_ref, new_value)
                                    } else {
                                        // `<=` compares a null max as 0; Number() says so, with the same result.
                                        if (new_value <= Number(vnode.attrs.max)) {
                                            modelref_assign(model_ref, new_value)
                                        } else {
                                            modelref_assign(model_ref, vnode.attrs.max)
                                        }
                                    }
                                }
                            } else if (e.key === 'Enter' && vnode.attrs.onenter) {
                                e.preventDefault()
                                vnode.attrs.onenter(e, Number(key_model_value))
                            }
                        }}
                        oninput={(e) => {
                            if (vnode.attrs.validation) {
                                vnode.attrs.validation.dirty = true
                            }

                            let {value} = e.currentTarget

                            if (value) {
                                if (vnode.attrs.max && Number(value) > vnode.attrs.max) {
                                    value = String(vnode.attrs.max)
                                }
                                if (vnode.attrs.min && Number(value) < vnode.attrs.min) {
                                    value = String(vnode.attrs.min)
                                }
                            }

                            // Round the number to the specified number of decimals to avoid floating point precision issues

                            if (vnode.attrs.decimals) {
                                value = Number(value).toFixed(vnode.attrs.decimals)
                            }
                            const num_value = value ? Number(value) : null
                            modelref_assign(model_ref, num_value)
                        }}
                        placeholder={vnode.attrs.placeholder ? vnode.attrs.placeholder : ''}
                        min={'min' in vnode.attrs ? vnode.attrs.min : undefined}
                        max={'max' in vnode.attrs ? vnode.attrs.max : undefined}
                        step={vnode.attrs.step ? vnode.attrs.step : 'any'}
                        tabindex={vnode.attrs.tabindex}
                        type='number'
                        value={model_value ?? ''}
                    />
                    {vnode.children}
                    {(() => {
                        if (invalid && validation?.dirty) {
                            return <div className='help validation'>{failure_message(invalid)}</div>
                        } else if (vnode.attrs.help) {
                            return <div class='help'>{vnode.attrs.help}</div>
                        }
                        return undefined
                    })()}
                </div>
            </div>
        )
    }
}
